/**
 * Emotion styles for the Cup O' Joe podcast landing page: the hero (`./Podcast.helpers.tsx` →
 * `PodcastHeroSection`, `SocialPlatformLinks`) and the episode grid (`./Podcasts.tsx`). Not used
 * by the episode page, `./PodcastDetail.tsx`, which styles inline.
 *
 * Most rules come in `*DesktopCSS` / `*MobileCSS` pairs for the two `Media` blocks (split at
 * `lg`). Functions take the values that vary: a theme colour, the background image, or the
 * desktop hero height (taller in French, see `./Podcasts.tsx`). The mobile hero is fixed at
 * 740px in both `HeroImageMobileCSS` and `HeroOverlayMobileCSS`; the 370px vertical padding in
 * `PodcastHeroSection` is half of it and must change with it.
 */
import { css } from "@emotion/react";
import styled from "@emotion/styled";

/** Desktop hero photo, `height` tall. Sits under {@link HeroOverlayDesktopCSS}. */
export const HeroImageDesktopCSS = (backgroundImage: string, height: string) => css`
	background-image: url(${backgroundImage});
	background-position: center center;
	background-size: cover;
	background-repeat: no-repeat;
	height: ${height};
	top: 0px;
	left: 0px;
`;

/** Mobile hero photo, 740px tall. */
export const HeroImageMobileCSS = (backgroundImage: string) => css`
	background-image: url(${backgroundImage});
	background-position: center center;
	background-size: cover;
	background-repeat: no-repeat;
	height: 740px;
	top: 0px;
	left: 0px;
	display: block;
`;

/** Desktop blue gradient over the photo, left to right, `height` tall; holds the hero content. */
export const HeroOverlayDesktopCSS = (height: string) => css`
	position: absolute;
	top: 0px;
	left: 0px;
	width: 100%;
	height: ${height};
	background: linear-gradient(
		90deg,
		rgba(0, 40, 85, 0.4) 18.5%,
		rgba(0, 104, 161, 0.731469) 51%,
		#009cde 77%
	);
	z-index: 1;
`;

/** Mobile blue gradient over the photo, top to bottom, 740px tall. */
export const HeroOverlayMobileCSS = css`
	position: absolute;
	top: 0px;
	left: 0px;
	width: 100%;
	height: 740px;
	z-index: 1;
	background: linear-gradient(
		180deg,
		rgba(0, 40, 85, 0.4) 42.06%,
		rgba(0, 104, 161, 0.731469) 53.55%,
		#009cde 77%
	);
`;

/** Desktop hero content column, centred, at most 1160px wide. */
export const HeroContentDesktopCSS = css`
	display: block;
	width: 100%;
	max-width: 1160px;
	margin: 0 auto;
	height: auto;
`;

/** Mobile hero content column with 20px side padding. */
export const HeroContentMobileCSS = css`
	display: block;
	width: 100%;
	margin: 0 auto;
	height: auto;
	padding-left: 20px;
	padding-right: 20px;
	box-sizing: border-box;
`;

/** Overrides {@link BreadcrumbContainer}'s top padding on desktop. */
export const BreadcrumbContainerDesktopCSS = css`
	padding-top: 36px;
`;

/** Overrides {@link BreadcrumbContainer}'s top padding on mobile. */
export const BreadcrumbContainerMobileCSS = css`
	padding-top: 28px;
`;

/** Overrides {@link HeroContainer}'s spacing on desktop. */
export const HeroContainerDesktopCSS = css`
	margin-bottom: 30px;
	padding-top: 65px;
`;

/** Overrides {@link HeroContainer}'s spacing on mobile. */
export const HeroContainerMobileCSS = css`
	margin-bottom: 30px;
	padding-top: 35px;
`;

/**
 * Desktop hero title. Its width leaves room for the logo ({@link PoweredBy}) on the right, and it
 * drops to 56px type at or below `breakpoint` (the theme's `xl`).
 */
export const HeroTitleDesktopCSS = (breakpoint: string) => css`
	margin-bottom: 20px;
	max-width: calc(100% - 240px);
	letter-spacing: -2px;
	@media (max-width: ${breakpoint}) {
		font-size: 56px;
	}
`;

/** Mobile hero title. */
export const HeroTitleMobileCSS = css`
	margin-bottom: 20px;
	letter-spacing: -2px;
`;

/** Desktop hero blurb; `pre-line` keeps line breaks in the copy. */
export const HeroBlurbDesktopCSS = css`
	margin: 0px;
	max-width: 760px;
	white-space: pre-line;
`;

/** Mobile hero blurb; `pre-line` keeps line breaks in the copy. */
export const HeroBlurbMobileCSS = css`
	margin: 0px;
	white-space: pre-line;
`;

/** Wrapper of the platform badges, shrunk to fit them. */
export const PlatformLinksContainerCSS = css`
	width: fit-content;
	position: relative;
`;

/** A platform badge image, spaced from the next. */
export const PlatformLinkCSS = css`
	display: inline-block;
	vertical-align: middle;
	margin-right: 20px;
`;

/** The last platform badge image, with no trailing space. */
export const PlatformLinkLastCSS = css`
	display: inline-block;
	vertical-align: middle;
`;

/** The Cup O' Joe logo below the mobile hero's button. */
export const PodcastLogoMobileCSS = css`
	display: block;
	width: 200px;
	height: auto;
	margin: 50px auto 0px auto;
`;

/** The "Episodes" heading above the grid. */
export const EpisodesTitleCSS = css`
	margin-bottom: 20px;
`;

/** Gap between episode cards on desktop. */
export const EpisodesGridDesktopCSS = css`
	gap: 40px;
`;

/** Gap between episode cards on mobile. */
export const EpisodesGridMobileCSS = css`
	gap: 16px;
`;

/** A desktop episode card: fixed 360 x 294px, on the theme's secondary background. */
export const EpisodeCardDesktopCSS = (backgroundColor: string) => css`
	width: 360px;
	height: 294px;
	background-color: ${backgroundColor};
	padding: 20px 50px;
	box-sizing: border-box;
`;

/** A mobile episode card: fixed 300 x 294px, on the theme's secondary background. */
export const EpisodeCardMobileCSS = (backgroundColor: string) => css`
	width: 300px;
	height: 294px;
	background-color: ${backgroundColor};
	padding: 20px;
	box-sizing: border-box;
`;

/** An episode card's thumbnail, cropped to 142px high. */
export const EpisodeThumbnailCSS = css`
	width: 100%;
	height: 142px;
	object-fit: cover;
	display: block;
	margin-bottom: 10px;
`;

/** An episode card's "Released on" line. */
export const EpisodeDateCSS = css`
	margin-bottom: 10px;
`;

/** An episode card's title. */
export const EpisodeTitleCSS = css`
	margin-bottom: 10px;
`;

/** The round chevron before "View Episode", in the brand colour. */
export const ViewEpisodeIconCSS = (primaryBrand: string) => css`
	display: inline-block;
	vertical-align: middle;
	width: 24px;
	height: 24px;
	text-align: center;
	line-height: 26px;
	border-radius: 24px;
	background-color: ${primaryBrand};
	margin-right: 10px;
`;

/** The "View Episode" label beside its chevron. */
export const ViewEpisodeTextCSS = css`
	display: inline-block;
	vertical-align: middle;
`;

/** Desktop spacing and grey border of the {@link LoadMore} pill. */
export const LoadMoreDesktopCSS = (grayColor: string) => css`
	margin-top: 80px;
	border: 1px solid ${grayColor};
`;

/** Mobile spacing and grey border of the {@link LoadMore} pill. */
export const LoadMoreMobileCSS = (grayColor: string) => css`
	margin-top: 40px;
	border: 1px solid ${grayColor};
`;

/**
 * Wraps the hero breadcrumb inside the overlay; `position: relative; z-index: 1` orders it only
 * among the overlay's children. Needs the theme as a prop for its `lg` breakpoint; the
 * `*DesktopCSS` / `*MobileCSS` overrides set the final padding.
 */
export const BreadcrumbContainer = styled.div`
	padding-top: 52px;
	position: relative;
	z-index: 1;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				padding-top: 28px;
			}
		`;
	}}
`;
/** The "Load More Episodes" pill button, centred, 210px wide. */
export const LoadMore = styled.div`
	display: block;
	margin: 0 auto;
	cursor: pointer;
	width: 210px;
	box-sizing: border-box;
	line-height: 36px;
	text-align: center;
	height: 40px;
	border-radius: 24px;
`;

/** The hero's title and blurb block; positioned so {@link PoweredBy} can sit at its top right. */
export const HeroContainer = styled.div`
	display: block;
	width: 100%;
	padding-top: 85px;
	box-sizing: border-box;
	height: auto;
	margin-bottom: 50px;
	position: relative;
`;

/** The absolutely positioned hero photo layer; its image and height come from `HeroImage*CSS`. */
export const HeroImage = styled.div`
	display: block;
	position: absolute;
	left: 0;
	width: 100%;
	height: 100%;
	background-repeat: no-repeat;
	background-position: center center;
	background-size: cover;
`;

/** The Cup O' Joe logo, 260px wide, at the desktop hero's top right. */
export const PoweredBy = styled.div`
	display: block;
	width: 260px;
	height: 285px;
	position: absolute;
	top: 10px;
	right: 0px;
	img {
		width: 100%;
		height: auto;
	}
`;
